import type { EChartsCoreOption } from 'echarts/core';

import {
  DataInterval,
  ReportsCategoryAxisOptions,
  ReportsTimeAxisOptions,
} from '@core/reports-types';

import { createChartMediaQueryRules } from '../media-query';
import { getBoundaryGap } from './boundary-gap';
import { formatSingleTickAxisLabel } from './date-format';

const MAX_MEDIA_QUERY_WIDTH = 1000;
const MEDIA_QUERY_STEP = 50;
// they take 13px at max, but bolded version is a bit wider
// 18 seems to work fine for majority of cases
const CHART_LABEL_WIDTH = 18;

// eslint-disable-next-line max-lines-per-function
export const daily = (
  ticksCount: number,
): {
  xAxis: ReportsCategoryAxisOptions | ReportsTimeAxisOptions;
  media: EChartsCoreOption['media'];
} => {
  const singleTickAxis = ticksCount === 1;
  const boundaryGap = getBoundaryGap(ticksCount);

  const formatterOpts = singleTickAxis
    ? formatSingleTickAxisLabel(DataInterval.Daily)
    : {
        day: '{d}',
        month: '{level1|{d}\n{MMM}}',
        year: '{level1|{d}\n{MMM} {yyyy}}',
        inherit: false,
      };

  // build array like [[0, 50], [50, 100], ... [800, INFINITY]]
  const mediaBreakpoints: Array<[number, number]> = [
    ...Array.from(
      { length: MAX_MEDIA_QUERY_WIDTH / MEDIA_QUERY_STEP + 1 },
      (_, i) => i * MEDIA_QUERY_STEP,
    ),
    Number.POSITIVE_INFINITY,
  ].reduce(
    (acc, curr, i, arr) => {
      if (i === 0) {
        return acc;
      }
      return [...acc, [arr[i - 1], curr]];
    },
    <Array<[number, number]>>[],
  );

  return {
    xAxis: {
      type: singleTickAxis ? 'category' : 'time',
      boundaryGap,
      axisTick: {
        alignWithLabel: true,
      },
      axisLabel: {
        fontSize: 12,
        rich: {
          primary: {
            top: 30,
          },
          level1: {
            fontWeight: 'bold',
          },
        },
        formatter: formatterOpts,
      },
    },
    media: createChartMediaQueryRules(
      mediaBreakpoints.map(([minW, maxW]) => {
        const maxWidth = Number.isFinite(minW) ? minW : 0;
        // approximately 100px on both left and right
        // The exact number is most likely available somewhere in the config, but it's hard to query here
        const drawAreaWidth = maxWidth - 100;

        const ticksCountBoundedToArea = Math.max(1, Math.ceil(drawAreaWidth / CHART_LABEL_WIDTH));
        let splitNumber: number;
        if (ticksCount === 0 || ticksCountBoundedToArea > ticksCount) {
          splitNumber = ticksCount;
        } else {
          // Find the closest number that divides ticksCount and is less than ticksCountBoundedToArea
          // this options draws ok only if the total number of ticks is nicely divided, eg. shows every 2nd item, 3rd, item, 5th item etc.
          let divideBy = 1;
          while (Math.ceil(ticksCount / divideBy) > ticksCountBoundedToArea) {
            divideBy += 1;
          }
          splitNumber = Math.ceil(ticksCount / divideBy);
        }

        return {
          minW,
          maxW,
          opts: {
            xAxis: {
              splitNumber,
            },
          },
        };
      }),
    ),
  };
};
